<script lang="ts">
	import { onMount } from 'svelte';
	import { calculateTilt, TILT_RESET } from '$lib/utils/tilt';
	import { prefersReducedMotion, isMobileDevice } from '$lib/utils/motion';

	let rotateX = $state(0);
	let rotateY = $state(0);
	let element: HTMLElement;

	function handleMouseMove(e: MouseEvent) {
		if (prefersReducedMotion() || isMobileDevice()) return;
		const tilt = calculateTilt(e, element, { maxAngle: 6 });
		rotateX = tilt.rotateX;
		rotateY = tilt.rotateY;
	}

	function handleMouseLeave() {
		rotateX = TILT_RESET.rotateX;
		rotateY = TILT_RESET.rotateY;
	}

	// Animated chart values
	let chartValues = $state([65, 45, 78, 52, 88, 42, 95, 68, 73, 85, 60, 92]);
	let oeeValue = $state(87.3);
	let alertPulse = $state(false);

	let chartPath = $derived(
		`M0,${120 - chartValues[0] * 1.1}` +
			` C33,${120 - chartValues[1] * 1.1} 33,${120 - chartValues[1] * 1.1} 66,${120 - chartValues[2] * 1.1}` +
			` C100,${120 - chartValues[3] * 1.1} 100,${120 - chartValues[3] * 1.1} 133,${120 - chartValues[4] * 1.1}` +
			` C166,${120 - chartValues[5] * 1.1} 166,${120 - chartValues[5] * 1.1} 200,${120 - chartValues[6] * 1.1}` +
			` C233,${120 - chartValues[7] * 1.1} 233,${120 - chartValues[7] * 1.1} 266,${120 - chartValues[8] * 1.1}` +
			` C300,${120 - chartValues[9] * 1.1} 300,${120 - chartValues[9] * 1.1} 333,${120 - chartValues[10] * 1.1}` +
			` C366,${120 - chartValues[11] * 1.1} 366,${120 - chartValues[11] * 1.1} 400,${120 - chartValues[11] * 1.1}`
	);

	let chartIntervalId: ReturnType<typeof setInterval> | null = null;
	let alertIntervalId: ReturnType<typeof setInterval> | null = null;

	function startIntervals() {
		if (chartIntervalId) return;
		const chartInterval = isMobileDevice() ? 5000 : 2000;
		chartIntervalId = setInterval(() => {
			chartValues = chartValues.map((v) => {
				const change = (Math.random() - 0.5) * 15;
				return Math.max(20, Math.min(100, v + change));
			});
			oeeValue = 85 + Math.random() * 5;
		}, chartInterval);

		alertIntervalId = setInterval(() => {
			alertPulse = !alertPulse;
		}, 1500);
	}

	function stopIntervals() {
		if (chartIntervalId) {
			clearInterval(chartIntervalId);
			chartIntervalId = null;
		}
		if (alertIntervalId) {
			clearInterval(alertIntervalId);
			alertIntervalId = null;
		}
	}

	onMount(() => {
		// Skip animations if user prefers reduced motion
		if (prefersReducedMotion()) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) startIntervals();
				else stopIntervals();
			},
			{ threshold: 0 }
		);
		observer.observe(element);

		return () => {
			observer.disconnect();
			stopIntervals();
		};
	});
</script>

<div
	bind:this={element}
	class="relative perspective-1000 w-full max-w-full overflow-hidden"
	onmousemove={handleMouseMove}
	onmouseleave={handleMouseLeave}
	role="presentation"
>
	<!-- Outer glow (hidden on mobile) -->
	<div
		class="hidden md:block absolute -inset-16 bg-gradient-to-br from-[#0086B7]/10 via-[#4ACB85]/5 to-transparent rounded-3xl blur-[80px] animate-pulse"
		style="animation-duration: 6s;"
	></div>

	<!-- Animated border gradient -->
	<div
		class="absolute -inset-px rounded-xl bg-gradient-to-br from-white/20 via-transparent to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
		style="mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); mask-composite: exclude; padding: 1px;"
	></div>

	<!-- Main dashboard -->
	<div
		class="dashboard-frame relative bg-slate-900/95 md:bg-slate-900/90 md:backdrop-blur-2xl rounded-xl border border-white/[0.1] overflow-hidden shadow-2xl shadow-black/30"
		style="transform: rotateX({rotateX}deg) rotateY({rotateY}deg); transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1);"
	>
		<!-- Browser chrome -->
		<div
			class="dashboard-chrome px-3 sm:px-4 py-2 sm:py-3 bg-white/[0.02] border-b border-white/[0.06] flex items-center gap-2 sm:gap-3"
		>
			<div class="flex gap-1 sm:gap-1.5">
				<div class="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-white/20"></div>
				<div class="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-white/20"></div>
				<div class="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-white/20"></div>
			</div>
			<div class="flex-1 flex justify-center min-w-0">
				<div
					class="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 bg-white/[0.03] rounded-md border border-white/[0.06] max-w-full"
				>
					<svg
						aria-hidden="true"
						class="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white/40 flex-shrink-0"
						fill="currentColor"
						viewBox="0 0 20 20"
					>
						<path
							fill-rule="evenodd"
							d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
							clip-rule="evenodd"
						/>
					</svg>
					<span class="text-[10px] sm:text-xs text-white/50 truncate">analytics.ameta.no</span>
				</div>
			</div>
		</div>

		<!-- Dashboard content -->
		<div class="p-3 sm:p-5 space-y-3 sm:space-y-4">
			<!-- Header -->
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-2 sm:gap-3">
					<div
						class="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-ameta-green flex items-center justify-center text-slate-950 font-bold text-xs sm:text-sm"
					>
						A
					</div>
					<div>
						<p class="text-xs sm:text-sm font-semibold text-slate-200">Production overview</p>
						<p class="text-[10px] sm:text-xs text-slate-500">Line 1, 2, 3 · Live</p>
					</div>
				</div>
				<div class="flex items-center gap-1.5">
					<span class="w-2 h-2 rounded-full bg-ameta-green animate-pulse"></span>
					<span class="text-[10px] sm:text-xs text-slate-400">Real-time</span>
				</div>
			</div>

			<!-- Stats grid -->
			<div class="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
				<div
					class="dashboard-stat p-2 sm:p-3 bg-slate-800/60 rounded-lg sm:rounded-xl border border-slate-700/30"
				>
					<p class="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wider mb-1">OEE</p>
					<p class="text-lg sm:text-xl font-bold text-ameta-green transition-all duration-500">
						{oeeValue.toFixed(1)}%
					</p>
					<p class="text-[9px] sm:text-[10px] text-green-400 flex items-center gap-0.5 mt-1">
						<svg
							aria-hidden="true"
							class="w-2.5 h-2.5 sm:w-3 sm:h-3"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M5 10l7-7m0 0l7 7m-7-7v18"
							/>
						</svg>
						2.3%
					</p>
				</div>
				<div
					class="dashboard-stat p-2 sm:p-3 bg-slate-800/60 rounded-lg sm:rounded-xl border border-slate-700/30"
				>
					<p class="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wider mb-1">
						Uptime
					</p>
					<p class="text-lg sm:text-xl font-bold text-slate-100">99.2%</p>
					<p class="text-[9px] sm:text-[10px] text-slate-500 mt-1">24h avg</p>
				</div>
				<div
					class="dashboard-stat p-2 sm:p-3 bg-slate-800/60 rounded-lg sm:rounded-xl border border-slate-700/30"
				>
					<p class="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wider mb-1">
						Output
					</p>
					<p class="text-lg sm:text-xl font-bold text-slate-100">1,247</p>
					<p class="text-[9px] sm:text-[10px] text-slate-500 mt-1">units/hr</p>
				</div>
				<div
					class="dashboard-stat p-2 sm:p-3 bg-slate-800/60 rounded-lg sm:rounded-xl border border-slate-700/30 {alertPulse
						? 'border-yellow-500/50'
						: ''}"
				>
					<p class="text-[9px] sm:text-[10px] text-slate-500 uppercase tracking-wider mb-1">
						Alerts
					</p>
					<p class="text-lg sm:text-xl font-bold text-yellow-500">3</p>
					<p class="text-[9px] sm:text-[10px] text-yellow-500/80 mt-1">1 critical</p>
				</div>
			</div>

			<!-- Chart -->
			<div
				class="dashboard-chart p-3 sm:p-4 bg-slate-800/40 rounded-lg sm:rounded-xl border border-slate-700/30"
			>
				<div class="flex items-center justify-between mb-2 sm:mb-3">
					<p class="text-[10px] sm:text-xs font-medium text-slate-300">Production rate</p>
					<div class="flex gap-1.5 sm:gap-2 text-[9px] sm:text-[10px]">
						<span class="text-slate-500">1h</span>
						<span class="text-ameta-green font-medium">24h</span>
						<span class="text-slate-500">7d</span>
					</div>
				</div>
				<div class="h-24 sm:h-32 relative">
					<svg
						aria-hidden="true"
						class="w-full h-full"
						viewBox="0 0 400 120"
						preserveAspectRatio="none"
					>
						<defs>
							<linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
								<stop offset="0%" stop-color="#4ACB85" stop-opacity="0.4" />
								<stop offset="100%" stop-color="#4ACB85" stop-opacity="0" />
							</linearGradient>
							<linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
								<stop offset="0%" stop-color="#4ACB85" stop-opacity="0.5" />
								<stop offset="50%" stop-color="#4ACB85" stop-opacity="1" />
								<stop offset="100%" stop-color="#4ACB85" stop-opacity="0.5" />
							</linearGradient>
							<filter id="glow">
								<feGaussianBlur stdDeviation="2" result="coloredBlur" />
								<feMerge>
									<feMergeNode in="coloredBlur" />
									<feMergeNode in="SourceGraphic" />
								</feMerge>
							</filter>
						</defs>
						<!-- Grid lines -->
						{#each [30, 60, 90] as y}
							<line
								x1="0"
								y1={y}
								x2="400"
								y2={y}
								stroke="rgba(255,255,255,0.05)"
								stroke-width="1"
							/>
						{/each}
						<!-- Area fill -->
						<path
							d="{chartPath} L400,120 L0,120 Z"
							fill="url(#chartGradient)"
							class="transition-all duration-1000 ease-out"
						/>
						<!-- Line -->
						<path
							d={chartPath}
							fill="none"
							stroke="url(#lineGradient)"
							stroke-width="2.5"
							filter="url(#glow)"
							class="transition-all duration-1000 ease-out"
						/>
						<!-- Animated dot at the end -->
						<circle
							cx="400"
							cy={120 - chartValues[11] * 1.1}
							r="4"
							fill="#4ACB85"
							filter="url(#glow)"
							class="transition-all duration-1000 ease-out"
						/>
						<circle
							cx="400"
							cy={120 - chartValues[11] * 1.1}
							r="6"
							fill="none"
							stroke="#4ACB85"
							stroke-width="1.5"
							opacity="0.5"
							class="animate-ping transition-all duration-1000"
						/>
					</svg>
					<!-- X-axis labels -->
					<div
						class="absolute bottom-0 left-0 right-0 flex justify-between text-[8px] sm:text-[9px] text-slate-600 px-0.5 sm:px-1 -mb-3 sm:-mb-4"
					>
						<span>0h</span>
						<span>6h</span>
						<span>12h</span>
						<span>18h</span>
						<span>24h</span>
					</div>
				</div>
			</div>

			<!-- Alert -->
			<div
				class="dashboard-alert p-3 bg-gradient-to-r from-yellow-500/10 to-transparent border border-yellow-500/30 rounded-xl flex items-center gap-2 sm:gap-3 {alertPulse
					? 'shadow-lg shadow-yellow-500/10'
					: ''} transition-shadow duration-500"
			>
				<div
					class="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0"
				>
					<svg
						aria-hidden="true"
						class="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
						/>
					</svg>
				</div>
				<div class="flex-1 min-w-0">
					<p class="text-xs sm:text-sm font-medium text-slate-200 truncate">
						Motor M2 vibration anomaly
					</p>
					<p class="text-[10px] sm:text-xs text-slate-500">Line 3 · Predicted failure in 48h</p>
				</div>
				<div class="hidden sm:flex items-center gap-2">
					<span
						class="px-2 py-1 text-[10px] font-semibold bg-ameta-green/20 text-ameta-green rounded-full whitespace-nowrap"
					>
						AI Predicted
					</span>
				</div>
			</div>
		</div>
	</div>
</div>
